import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Avatar,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { t } from "../i18n";
import { api } from "../apiClient";
import {
  destructiveButtonSx,
  dialogSx as baseDialogSx,
  fieldSx as baseFieldSx,
  outlinedButtonSx,
  panelSx,
  primaryButtonSx,
  secondaryButtonSx,
  sectionLeadSx,
  sectionTitleSx,
  shellCardSx,
} from "../uiStyles";

const navItems = [
  { key: "general", label: { en: "General", ru: "Основные" } },
  { key: "users", label: { en: "Users", ru: "Пользователи" } },
  { key: "security", label: { en: "Security", ru: "Безопасность" } },
  { key: "analytics", label: { en: "Analytics", ru: "Аналитика" } },
  { key: "email", label: { en: "Email", ru: "Эл. почта" } },
  { key: "templates", label: { en: "Notification templates", ru: "Шаблоны уведомлений" } },
  { key: "import-export", label: { en: "Import and export", ru: "Импорт и экспорт" } },
  { key: "update", label: { en: "Update", ru: "Обновление" } },
];

const dialogSx = {
  ...baseDialogSx,
  "& .MuiDialog-paper": {
    ...baseDialogSx["& .MuiDialog-paper"],
    width: "min(560px, calc(100vw - 32px))",
  },
};

const noSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

const contentColumnSx = {
  width: "100%",
  maxWidth: { xs: "100%", xl: 1180 },
  mx: "auto",
};

const formBlockSx = {
  width: "100%",
  border: "1px solid rgba(17,24,39,0.08)",
  borderRadius: 3,
  p: { xs: 1.75, md: 2.25 },
  bgcolor: "background.default",
  mx: "auto",
};

const formFieldWrapSx = {
  width: "100%",
};

function themedPrimaryButtonSx(isDark) {
  return isDark
    ? {
        ...primaryButtonSx,
        bgcolor: "#7a746b",
        "&:hover": { bgcolor: "#625d56", boxShadow: "none" },
        "&.Mui-disabled": { bgcolor: "rgba(122,116,107,0.42)", color: "rgba(255,255,255,0.82)", boxShadow: "none" },
      }
    : primaryButtonSx;
}

function themedSecondaryButtonSx(isDark) {
  return isDark ? { ...secondaryButtonSx, color: "rgba(226,232,240,0.88)" } : secondaryButtonSx;
}

function themedOutlinedButtonSx(isDark) {
  return isDark ? { ...outlinedButtonSx, color: "#e5e7eb", borderColor: "rgba(148,163,184,0.22)" } : outlinedButtonSx;
}

function sectionFrame(children) {
  return children;
}

function CreateUserDialog({ open, currentLocale, currentTheme = "light", form, onClose, onChange, onCreate, loading }) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t("admin.newUser", currentLocale)}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75, mb: 2 }}>{t("admin.addUserText", currentLocale)}</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 0.5 }}>
        <TextField label={currentLocale === "en" ? "Name" : "Имя"} value={form.displayName || ""} onChange={(e) => onChange("displayName", e.target.value)} fullWidth margin="normal" sx={{ ...baseFieldSx(isDark), mt: 1.5 }} />
        <TextField label={t("common.email", currentLocale)} value={form.email || ""} onChange={(e) => onChange("email", e.target.value)} fullWidth sx={baseFieldSx(isDark)} />
        <FormControl fullWidth sx={baseFieldSx(isDark)}>
          <InputLabel>{t("common.role", currentLocale)}</InputLabel>
          <Select label={t("common.role", currentLocale)} value={form.role || "user"} onChange={(e) => onChange("role", e.target.value)}>
            <MenuItem value="admin">admin</MenuItem>
            <MenuItem value="user">user</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox checked={!!form.sendInvitation} onChange={(e) => onChange("sendInvitation", e.target.checked)} />}
          label={currentLocale === "en" ? "Send invitation link by email" : "Отправить ссылку-приглашение по электронной почте"}
          sx={{ mx: 0 }}
        />
        {!form.sendInvitation ? (
          <>
            <TextField type="password" label={t("common.password", currentLocale)} value={form.password || ""} onChange={(e) => onChange("password", e.target.value)} fullWidth sx={baseFieldSx(isDark)} />
            <TextField type="password" label={t("admin.repeatPassword", currentLocale)} value={form.confirmPassword || ""} onChange={(e) => onChange("confirmPassword", e.target.value)} fullWidth sx={baseFieldSx(isDark)} />
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25, width: "100%" }}>
          <Button onClick={onClose} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>{t("common.cancel", currentLocale)}</Button>
          <Button variant="contained" onClick={onCreate} disabled={loading || !String(form.displayName || "").trim() || !String(form.email || "").trim() || (!form.sendInvitation && (!form.password || form.password !== form.confirmPassword))} sx={themedPrimaryButtonSx(isDark)}>{t("common.create", currentLocale)}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function PasswordDialog({ open, currentLocale, currentTheme = "light", form, onClose, onPasswordChange, onConfirmChange, onSave, loading }) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t("admin.changePassword", currentLocale)}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>{t("admin.passwordDialogText", currentLocale)}</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1.75 }}>
        <Box sx={{ px: 1.5, py: 1.15, borderRadius: 2.5, bgcolor: "rgba(107,114,128,0.08)", border: "1px solid rgba(17,24,39,0.06)" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>{form.email || ""}</Typography>
        </Box>
        <TextField type="password" label={t("common.password", currentLocale)} value={form.password || ""} onChange={(e) => onPasswordChange(form.email, e.target.value)} fullWidth sx={baseFieldSx(isDark)} />
        <TextField type="password" label={t("admin.repeatPassword", currentLocale)} value={form.confirmPassword || ""} onChange={(e) => onConfirmChange(form.email, e.target.value)} fullWidth sx={baseFieldSx(isDark)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25, width: "100%" }}>
          <Button onClick={onClose} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>{t("common.cancel", currentLocale)}</Button>
          <Button variant="contained" onClick={onSave} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{t("common.save", currentLocale)}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({ open, currentLocale, currentTheme = "light", email, onClose, onConfirm, loading }) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t("admin.deleteUser", currentLocale)}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>{t("admin.deleteDialogText", currentLocale)}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ px: 1.75, py: 1.4, borderRadius: 3, bgcolor: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.12)" }}>
          <Typography sx={{ mb: 0.5, fontWeight: 600 }}>{t("admin.deleteUserConfirm", currentLocale)}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", wordBreak: "break-word" }}>{email}</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25, width: "100%" }}>
          <Button onClick={onClose} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>{t("common.cancel", currentLocale)}</Button>
          <Button onClick={onConfirm} variant="contained" disabled={loading} sx={destructiveButtonSx}>{t("admin.deleteUser", currentLocale)}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function UsersSection(props) {
  const { currentLocale, currentTheme = "light", currentUserEmail, users, loading, onOpenNewUser, onRoleChange, onOpenPasswordDialog, onOpenDeleteDialog } = props;
  const avatarBg = currentTheme === "dark" ? "#9a948b" : "#9ca3af";
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Box sx={{ ...contentColumnSx, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={sectionTitleSx}>{currentLocale === "en" ? "Users" : "Пользователи"}</Typography>
          <Typography variant="body2" sx={sectionLeadSx}>{currentLocale === "en" ? "User accounts, roles, and password management." : "Список учетных записей, роли доступа и управление паролями."}</Typography>
        </Box>
        <Tooltip title={currentLocale === "en" ? "New user" : "Новый пользователь"} arrow>
          <IconButton onClick={onOpenNewUser} sx={{ border: "1px solid rgba(17,24,39,0.12)", borderRadius: 999, color: "text.primary" }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Stack spacing={1.5} sx={contentColumnSx}>
        {users.map((user) => {
          const isCurrentUser = (user.email || "").toLowerCase() === (currentUserEmail || "").toLowerCase();
          const displayName = (user.displayName || "").trim() || user.email;
          const initials = displayName.trim().charAt(0).toUpperCase() || "U";
          return (
            <Box key={user.email} sx={{ border: "1px solid rgba(17,24,39,0.08)", borderRadius: 3, p: { xs: 1.25, sm: 1.5 }, bgcolor: "background.default" }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
                <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                  <Avatar src={user.avatarUrl || ""} sx={{ width: { xs: 40, sm: 44 }, height: { xs: 40, sm: 44 }, bgcolor: avatarBg, color: "#fff", fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{initials}</Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, lineHeight: 1.2, wordBreak: "break-word" }}>{displayName}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35, fontSize: { xs: 13, sm: 14 }, lineHeight: 1.35, wordBreak: "break-word" }}>{user.email}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.85, flexWrap: "wrap", rowGap: 0.75 }}>
                      <Chip label={user.role === "admin" ? (currentLocale === "en" ? "Administrator" : "Администратор") : (currentLocale === "en" ? "User" : "Пользователь")} size="small" color={user.role === "admin" ? "primary" : "default"} />
                      {user.status === "pending" ? <Chip label={currentLocale === "en" ? "Pending" : "Ожидается"} size="small" color="warning" variant="outlined" /> : null}
                      <Tooltip title={currentLocale === "en" ? "Last sign in" : "Последний вход"} arrow>
                        <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.35, cursor: "help" }}>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : "-"}</Typography>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" }, alignItems: { xs: "stretch", sm: "center" } }}>
                  <Tooltip title={isCurrentUser ? t("admin.cannotChangeOwnRole", currentLocale) : t("common.role", currentLocale)} arrow>
                    <span>
                      <FormControl size="small" sx={{ width: { xs: "100%", sm: 172 } }}>
                        <InputLabel id={`role-label-${user.email}`}>{currentLocale === "en" ? "Role" : "Роль"}</InputLabel>
                        <Select labelId={`role-label-${user.email}`} label={currentLocale === "en" ? "Role" : "Роль"} value={user.role} onChange={(event) => onRoleChange(user.email, event.target.value)} disabled={isCurrentUser}>
                          <MenuItem value="admin">admin</MenuItem>
                          <MenuItem value="user">user</MenuItem>
                        </Select>
                      </FormControl>
                    </span>
                  </Tooltip>
                  <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <Tooltip title={isCurrentUser ? t("admin.cannotChangeOwnPasswordHere", currentLocale) : (currentLocale === "en" ? "Change password" : "Сменить пароль")} arrow>
                      <span style={{ display: "flex", width: "100%" }}>
                        <IconButton onClick={() => onOpenPasswordDialog(user.email)} disabled={loading || isCurrentUser || user.status === "pending"} sx={{ border: "1px solid rgba(17,24,39,0.12)", borderRadius: 999, color: "text.primary", width: { xs: "100%", sm: 42 }, height: 42 }}><LockResetIcon /></IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isCurrentUser ? t("admin.cannotDeleteOwnAccount", currentLocale) : (currentLocale === "en" ? "Delete user" : "Удалить пользователя")} arrow>
                      <span style={{ display: "flex", width: "100%" }}>
                        <IconButton onClick={() => onOpenDeleteDialog(user.email)} disabled={loading || isCurrentUser} sx={{ border: "1px solid rgba(220,38,38,0.16)", borderRadius: 999, color: "error.main", width: { xs: "100%", sm: 42 }, height: 42 }}><DeleteOutlineIcon /></IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function SecuritySection({ currentLocale, currentTheme = "light", loading, allowRememberMe, enforceTwoFactor, sessionDurationMinutes, profileShareTtlMinutes, onSecuritySettingsChange, onSecuritySettingsSave }) {
  const isDark = currentTheme === "dark";
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{currentLocale === "en" ? "Security" : "Безопасность"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{currentLocale === "en" ? "Configure persistent sign-in and the lifetime of regular sessions." : "Настройте постоянный вход и время жизни обычной сессии."}</Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
            <Box sx={{ pr: { sm: 2 }, maxWidth: 760 }}>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Allow users to stay signed in" : "Разрешить запоминание входа"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Users will be able to keep their sign-in between browser sessions." : "Пользователи смогут сохранять вход между сессиями браузера."}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>{loading ? <CircularProgress size={20} sx={{ color: "#6b7280" }} /> : <Switch checked={!!allowRememberMe} onChange={(event) => { onSecuritySettingsChange?.("allowRememberMe", event.target.checked); onSecuritySettingsSave?.({ allowRememberMe: event.target.checked, sessionDurationMinutes }); }} />}</Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
            <Box sx={{ pr: { sm: 2 }, maxWidth: 760 }}>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Force two-factor authentication" : "Форсировать двухфакторную авторизацию"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Require an email verification code for every user sign-in." : "Требовать код подтверждения по почте для входа всех пользователей."}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>{loading ? <CircularProgress size={20} sx={{ color: "#6b7280" }} /> : <Switch checked={!!enforceTwoFactor} onChange={(event) => { onSecuritySettingsChange?.("enforceTwoFactor", event.target.checked); onSecuritySettingsSave?.({ enforceTwoFactor: event.target.checked, allowRememberMe, sessionDurationMinutes }); }} />}</Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Session timeout without remember me" : "Таймаут сессии без запоминания входа"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Determines how long a normal session remains active when the user does not choose persistent sign-in." : "Определяет, сколько живёт сессия, если пользователь не выбрал запоминание входа."}</Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "flex-end" }}>
              <Box sx={formFieldWrapSx}>
                <TextField label={currentLocale === "en" ? "Minutes" : "Минуты"} type="number" value={sessionDurationMinutes ?? ""} onChange={(event) => onSecuritySettingsChange?.("sessionDurationMinutes", event.target.value)} inputProps={{ min: 1, max: 10080 }} sx={{ ...baseFieldSx(isDark), ...noSpinnerSx }} fullWidth />
              </Box>
              <Button type="button" variant="contained" onClick={() => onSecuritySettingsSave?.({ allowRememberMe, enforceTwoFactor, sessionDurationMinutes, profileShareTtlMinutes })} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Apply" : "Применить"}</Button>
            </Stack>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Profile share link lifetime" : "Время жизни ссылки на профиль"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Default lifetime for public profile download links." : "Время жизни публичной ссылки для скачивания профиля по умолчанию."}</Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "flex-end" }}>
              <Box sx={formFieldWrapSx}>
                <TextField label={currentLocale === "en" ? "Minutes" : "Минуты"} type="number" value={profileShareTtlMinutes ?? ""} onChange={(event) => onSecuritySettingsChange?.("profileShareTtlMinutes", event.target.value)} inputProps={{ min: 1, max: 10080 }} sx={{ ...baseFieldSx(isDark), ...noSpinnerSx }} fullWidth />
              </Box>
              <Button type="button" variant="contained" onClick={() => onSecuritySettingsSave?.({ allowRememberMe, enforceTwoFactor, sessionDurationMinutes, profileShareTtlMinutes })} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Apply" : "Применить"}</Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function GeneralSection({ currentLocale, currentTheme = "light", loading, defaultTheme, defaultLocale, onSecuritySettingsChange, onSecuritySettingsSave }) {
  const isDark = currentTheme === "dark";
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{currentLocale === "en" ? "General" : "Основные"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{currentLocale === "en" ? "Default appearance and language for the login screen and new users." : "Язык и тема по умолчанию для формы входа и новых пользователей."}</Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Default language" : "Язык по умолчанию"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Used on the login screen and assigned to newly created users." : "Используется на форме входа и назначается новым пользователям."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <FormControl fullWidth sx={baseFieldSx(isDark)}>
                <InputLabel>{currentLocale === "en" ? "Language" : "Язык"}</InputLabel>
                <Select label={currentLocale === "en" ? "Language" : "Язык"} value={defaultLocale || "ru"} onChange={(event) => { const value = event.target.value; onSecuritySettingsChange?.("defaultLocale", value); onSecuritySettingsSave?.({ defaultLocale: value, defaultTheme }); }} disabled={loading}>
                  <MenuItem value="ru">Русский</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Default theme" : "Тема по умолчанию"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Applied to the login screen and to accounts created later." : "Применяется к форме входа и к учётным записям, созданным позже."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <FormControl fullWidth sx={baseFieldSx(isDark)}>
                <InputLabel>{currentLocale === "en" ? "Theme" : "Тема"}</InputLabel>
                <Select label={currentLocale === "en" ? "Theme" : "Тема"} value={defaultTheme || "light"} onChange={(event) => { const value = event.target.value; onSecuritySettingsChange?.("defaultTheme", value); onSecuritySettingsSave?.({ defaultTheme: value, defaultLocale }); }} disabled={loading}>
                  <MenuItem value="system">{currentLocale === "en" ? "System" : "Системная"}</MenuItem>
                  <MenuItem value="light">{currentLocale === "en" ? "Light" : "Светлая"}</MenuItem>
                  <MenuItem value="dark">{currentLocale === "en" ? "Dark" : "Тёмная"}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function AnalyticsSection({ currentLocale, currentTheme = "light", loading, settings, onSecuritySettingsChange, onSecuritySettingsSave }) {
  const isDark = currentTheme === "dark";
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{currentLocale === "en" ? "Profile analytics" : "Аналитика профилей"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{currentLocale === "en" ? "Quantitative traffic history for the overview and profile dashboards." : "Количественная история трафика для общего дашборда и карточек профилей."}</Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
            <Box><Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Collect traffic analytics" : "Собирать аналитику трафика"}</Typography><Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "When disabled, new traffic measurements are not stored." : "При выключении новые замеры объёма трафика не сохраняются."}</Typography></Box>
            <Switch checked={!!settings?.analyticsEnabled} disabled={loading} onChange={(event) => { onSecuritySettingsChange?.("analyticsEnabled", event.target.checked); onSecuritySettingsSave?.({ analyticsEnabled: event.target.checked, analyticsRetentionDays: settings?.analyticsRetentionDays || 7 }); }} />
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
            <Box><Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Show analytics panel on the home screen" : "Отображать панель аналитики на главном экране"}</Typography><Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Hides only the overview panel; profile analytics remain available." : "Скрывает только общую панель; аналитика отдельных профилей остаётся доступна."}</Typography></Box>
            <Switch checked={settings?.analyticsPanelVisible !== false} disabled={loading || !settings?.analyticsEnabled} onChange={(event) => { onSecuritySettingsChange?.("analyticsPanelVisible", event.target.checked); onSecuritySettingsSave?.({ analyticsPanelVisible: event.target.checked }); }} />
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{currentLocale === "en" ? "Analysis depth" : "Глубина анализа"}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{currentLocale === "en" ? "Data older than this period is deleted automatically." : "Данные старше этого периода удаляются автоматически."}</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "flex-end" }}>
            <TextField label={currentLocale === "en" ? "Days" : "Дней"} type="number" value={settings?.analyticsRetentionDays ?? 7} disabled={!settings?.analyticsEnabled} onChange={(event) => onSecuritySettingsChange?.("analyticsRetentionDays", event.target.value)} inputProps={{ min: 1, max: 365 }} sx={{ ...baseFieldSx(isDark), ...noSpinnerSx }} fullWidth />
            <Button variant="contained" disabled={loading || !settings?.analyticsEnabled} onClick={() => onSecuritySettingsSave?.({ analyticsEnabled: settings?.analyticsEnabled, analyticsRetentionDays: settings?.analyticsRetentionDays || 7 })} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Apply" : "Применить"}</Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function EmailSection({ currentLocale, currentTheme = "light", loading, settings, onSecuritySettingsChange, onSecuritySettingsSave, onCheckEmailSettings, onOpenTestEmailDialog }) {
  const isDark = currentTheme === "dark";
  const [showPassword, setShowPassword] = useState(false);
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{currentLocale === "en" ? "Email" : "Эл. почта"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{currentLocale === "en" ? "Configure SMTP connection settings. These values override the environment defaults." : "Настройте параметры SMTP-подключения. Эти значения переопределяют настройки из env."}</Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack spacing={1.75}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Protocol settings" : "Настройка протокола"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Connection parameters for the SMTP server." : "Параметры подключения к SMTP-серверу."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}><TextField label="SMTP Host" value={settings.smtpHost || ""} onChange={(event) => onSecuritySettingsChange?.("smtpHost", event.target.value)} fullWidth sx={baseFieldSx(isDark)} /></Box>
            <Box sx={formFieldWrapSx}><TextField label="SMTP Port" type="number" value={settings.smtpPort ?? ""} onChange={(event) => onSecuritySettingsChange?.("smtpPort", event.target.value)} fullWidth sx={{ ...baseFieldSx(isDark), ...noSpinnerSx }} /></Box>
            <Box sx={formFieldWrapSx}>
              <FormControl fullWidth sx={baseFieldSx(isDark)}>
                <InputLabel>{currentLocale === "en" ? "Connection security" : "Защита соединения"}</InputLabel>
                <Select label={currentLocale === "en" ? "Connection security" : "Защита соединения"} value={settings.smtpSecurity || "starttls"} onChange={(event) => onSecuritySettingsChange?.("smtpSecurity", event.target.value)}>
                  <MenuItem value="starttls">STARTTLS</MenuItem>
                  <MenuItem value="ssl">SSL/TLS</MenuItem>
                  <MenuItem value="none">{currentLocale === "en" ? "No TLS" : "Без TLS"}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={formFieldWrapSx}><TextField label="SMTP User" value={settings.smtpUser || ""} onChange={(event) => onSecuritySettingsChange?.("smtpUser", event.target.value)} fullWidth sx={baseFieldSx(isDark)} /></Box>
            <Box sx={formFieldWrapSx}><TextField label="SMTP Password" type={showPassword ? "text" : "password"} value={settings.smtpPassword || ""} onChange={(event) => onSecuritySettingsChange?.("smtpPassword", event.target.value)} fullWidth sx={baseFieldSx(isDark)} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">{showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}</IconButton></InputAdornment> }} /></Box>
            <Box sx={{ ...formFieldWrapSx, display: "flex", justifyContent: "flex-end" }}>
              <Button type="button" variant="outlined" onClick={() => onCheckEmailSettings?.()} disabled={loading} sx={themedOutlinedButtonSx(isDark)}>{currentLocale === "en" ? "Check" : "Проверить"}</Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.75}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Sender settings" : "Параметры отправителя"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "The address and name that recipients will see." : "Адрес и имя, которые увидит получатель письма."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "From email" : "Email отправителя"} value={settings.smtpFromEmail || ""} onChange={(event) => onSecuritySettingsChange?.("smtpFromEmail", event.target.value)} fullWidth sx={baseFieldSx(isDark)} /></Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "From name" : "Имя отправителя"} value={settings.smtpFromName || ""} onChange={(event) => onSecuritySettingsChange?.("smtpFromName", event.target.value)} fullWidth sx={baseFieldSx(isDark)} /></Box>
            <Box sx={{ ...formFieldWrapSx, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button type="button" variant="outlined" onClick={() => onOpenTestEmailDialog?.()} disabled={loading} sx={themedOutlinedButtonSx(isDark)}>{currentLocale === "en" ? "Send test email" : "Отправить тестовое"}</Button>
              <Button type="button" variant="contained" onClick={() => onSecuritySettingsSave?.()} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Save email settings" : "Сохранить настройки почты"}</Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function TemplatesSection({ currentLocale, currentTheme = "light", loading, settings, onSecuritySettingsChange, onSecuritySettingsSave }) {
  const isDark = currentTheme === "dark";
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{currentLocale === "en" ? "Notification templates" : "Шаблоны уведомлений"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{currentLocale === "en" ? "Manage email templates for future notifications." : "Управляйте шаблонами писем для текущих и будущих уведомлений."}</Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Password reset email" : "Письмо восстановления пароля"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Template for the password reset message." : "Шаблон письма для восстановления пароля."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email subject" : "Тема письма"} value={settings.passwordResetSubject || ""} onChange={(event) => onSecuritySettingsChange?.("passwordResetSubject", event.target.value)} fullWidth sx={baseFieldSx(isDark)} /></Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email body" : "Текст письма"} value={settings.passwordResetBodyTemplate || ""} onChange={(event) => onSecuritySettingsChange?.("passwordResetBodyTemplate", event.target.value)} multiline minRows={5} fullWidth sx={baseFieldSx(isDark)} helperText={currentLocale === "en" ? "Use {code} where the reset code should appear." : "Используйте {code} в месте, где должен подставляться код восстановления."} /></Box>
            <Box sx={{ ...formFieldWrapSx, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button type="button" variant="contained" onClick={() => onSecuritySettingsSave?.()} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Save template" : "Сохранить шаблон"}</Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Login verification / 2FA email" : "Письмо подтверждения входа / 2FA"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Template for login confirmation and two-factor verification emails." : "Шаблон письма для подтверждения входа и двухфакторной аутентификации."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email subject" : "Тема письма"} value={settings.loginVerificationSubject || ""} onChange={(event) => onSecuritySettingsChange?.("loginVerificationSubject", event.target.value)} fullWidth sx={baseFieldSx(isDark)} /></Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email body" : "Текст письма"} value={settings.loginVerificationBodyTemplate || ""} onChange={(event) => onSecuritySettingsChange?.("loginVerificationBodyTemplate", event.target.value)} multiline minRows={5} fullWidth sx={baseFieldSx(isDark)} helperText={currentLocale === "en" ? "Use {code} where the verification code should appear." : "Используйте {code} в месте, где должен подставляться код подтверждения."} /></Box>
            <Box sx={{ ...formFieldWrapSx, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button type="button" variant="contained" onClick={() => onSecuritySettingsSave?.()} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Save template" : "Сохранить шаблон"}</Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "User invitation email" : "Письмо-приглашение пользователя"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Sent when a user is created with an invitation link." : "Отправляется при создании пользователя по ссылке-приглашению."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email subject" : "Тема письма"} value={settings.invitationSubject || ""} onChange={(event) => onSecuritySettingsChange?.("invitationSubject", event.target.value)} fullWidth sx={baseFieldSx(isDark)} /></Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email body" : "Текст письма"} value={settings.invitationBodyTemplate || ""} onChange={(event) => onSecuritySettingsChange?.("invitationBodyTemplate", event.target.value)} multiline minRows={5} fullWidth sx={baseFieldSx(isDark)} helperText={currentLocale === "en" ? "Use {link} for the invitation link and {email} for the recipient address." : "Используйте {link} для ссылки-приглашения и {email} для адреса получателя."} /></Box>
            <Box sx={{ ...formFieldWrapSx, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button type="button" variant="contained" onClick={() => onSecuritySettingsSave?.()} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Save template" : "Сохранить шаблон"}</Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "Profile download link email" : "Письмо со ссылкой на скачивание профиля"}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{currentLocale === "en" ? "Used when a profile link is sent after creation or from the Share dialog." : "Используется при отправке ссылки после создания профиля и из окна «Поделиться»."}</Typography>
            </Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email subject" : "Тема письма"} value={settings.profileLinkSubject || ""} onChange={(event) => onSecuritySettingsChange?.("profileLinkSubject", event.target.value)} fullWidth sx={baseFieldSx(isDark)} helperText={currentLocale === "en" ? "You can use {profile_name}." : "Можно использовать {profile_name}."} /></Box>
            <Box sx={formFieldWrapSx}><TextField label={currentLocale === "en" ? "Email body" : "Текст письма"} value={settings.profileLinkBodyTemplate || ""} onChange={(event) => onSecuritySettingsChange?.("profileLinkBodyTemplate", event.target.value)} multiline minRows={5} fullWidth sx={baseFieldSx(isDark)} helperText={currentLocale === "en" ? "Use {link} for the download link and {profile_name} for the profile name." : "Используйте {link} для ссылки на скачивание и {profile_name} для имени профиля."} /></Box>
            <Box sx={{ ...formFieldWrapSx, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button type="button" variant="contained" onClick={() => onSecuritySettingsSave?.()} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Save template" : "Сохранить шаблон"}</Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function ImportExportSection({ currentLocale, currentTheme = "light", onImported }) {
  const isDark = currentTheme === "dark";
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [sendEmails, setSendEmails] = useState(false);

  const closeImport = () => {
    if (busy) return;
    setImportOpen(false);
    setImportFile(null);
    setSendEmails(false);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setBusy(true);
    setResult(null);
    try {
      const content = await importFile.text();
      const response = await api.post("/admin/awg/profiles/import", { content, sendEmails }, { headers: { "Accept-Language": currentLocale } });
      setResult(response.data);
      if (response.data?.createdCount) await onImported?.();
      setImportOpen(false);
      setImportFile(null);
      setSendEmails(false);
    } catch (error) {
      setResult({ createdCount: 0, errorCount: 1, errors: [{ error: error.response?.data?.error || error.message }] });
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    setBusy(true);
    try {
      const response = await api.get("/admin/awg/profiles/export", { responseType: "blob", headers: { "Accept-Language": currentLocale } });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "ocean-users.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{currentLocale === "en" ? "Import and export" : "Импорт и экспорт"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{currentLocale === "en" ? "Bulk-create user profiles from CSV or export the current list." : "Массовое добавление пользовательских профилей из CSV и выгрузка текущего списка."}</Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? "CSV format" : "Формат CSV"}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{currentLocale === "en" ? "Example of data to import:" : "Пример импортируемых данных:"}</Typography>
            <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(17,24,39,0.04)", overflowX: "auto", fontSize: 14 }}>Иван Иванов;ivan@example.ru{"\n"}Пётр Петров;petr@example.ru</Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={() => setImportOpen(true)} disabled={busy} sx={themedPrimaryButtonSx(isDark)}>
                {currentLocale === "en" ? "Import" : "Импорт"}
              </Button>
              <Button variant="outlined" onClick={handleExport} disabled={busy} sx={{ ...themedOutlinedButtonSx(isDark), minWidth: 112 }}>
                {busy ? <CircularProgress size={20} color="inherit" /> : (currentLocale === "en" ? "Export" : "Экспорт")}
              </Button>
            </Stack>
          </Stack>
        </Box>
        {result ? (
          <Box sx={formBlockSx}>
            <Typography sx={{ fontWeight: 600 }}>{currentLocale === "en" ? `Created: ${result.createdCount || 0}; errors: ${result.errorCount || 0}` : `Добавлено: ${result.createdCount || 0}; ошибок: ${result.errorCount || 0}`}</Typography>
            {result.emailSentCount ? <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary" }}>{currentLocale === "en" ? `Emails sent: ${result.emailSentCount}` : `Отправлено писем: ${result.emailSentCount}`}</Typography> : null}
            {[...(result.errors || []), ...(result.emailErrors || [])].length ? <Stack spacing={0.75} sx={{ mt: 1.25 }}>{[...(result.errors || []), ...(result.emailErrors || [])].map((item, index) => <Typography key={`${item.line || 0}-${index}`} variant="body2" color="error">{item.line ? `${currentLocale === "en" ? "Line" : "Строка"} ${item.line}: ` : ""}{item.error}</Typography>)}</Stack> : null}
          </Box>
        ) : null}
      </Stack>
      <Dialog open={importOpen} onClose={closeImport} fullWidth maxWidth="sm" sx={dialogSx}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{currentLocale === "en" ? "Import profiles" : "Импорт профилей"}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>{currentLocale === "en" ? "Select a CSV file." : "Выберите CSV-файл"}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={2}>
            <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon />} disabled={busy} sx={{ ...themedOutlinedButtonSx(isDark), alignSelf: "flex-start" }}>
              {currentLocale === "en" ? "Upload CSV" : "Загрузить CSV"}
              <input hidden type="file" accept=".csv,text/csv,text/plain" onChange={(event) => { setImportFile(event.target.files?.[0] || null); event.target.value = ""; }} />
            </Button>
            {importFile ? <Typography variant="body2" sx={{ color: "text.secondary", wordBreak: "break-word" }}>{importFile.name}</Typography> : null}
            <FormControlLabel
              control={<Checkbox checked={sendEmails} onChange={(event) => setSendEmails(event.target.checked)} disabled={busy} />}
              label={currentLocale === "en" ? "Email profile links to users" : "Отправить пользователям ссылки на профили по электронной почте"}
              sx={{ alignItems: "flex-start", m: 0, "& .MuiFormControlLabel-label": { pt: 1 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25, width: "100%" }}>
            <Button onClick={closeImport} disabled={busy} sx={themedSecondaryButtonSx(isDark)}>{currentLocale === "en" ? "Cancel" : "Отмена"}</Button>
            <Button variant="contained" onClick={handleImport} disabled={busy || !importFile} sx={themedPrimaryButtonSx(isDark)}>
              {busy ? <CircularProgress size={20} color="inherit" /> : (currentLocale === "en" ? "Import" : "Импортировать")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function UpdateSection({ currentLocale, currentTheme }) {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isEnglish = currentLocale === "en";
  const isDark = currentTheme === "dark";
  const job = updateInfo?.job || {};
  const jobActive = ["queued", "running", "rollback"].includes(job.status);

  const loadUpdate = async (force = false) => {
    setBusy(true);
    setError("");
    try {
      const response = force ? await api.post("/admin/update/check") : await api.get("/admin/update");
      setUpdateInfo(response.data);
      if (response.data?.checkError) setError(response.data.checkError);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadUpdate(false); }, []);
  useEffect(() => {
    if (!jobActive) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const response = await api.get("/admin/update/status");
        setUpdateInfo((current) => ({ ...(current || {}), ...response.data }));
      } catch (_) {
        // The backend is briefly unavailable while its container is replaced.
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [jobActive]);
  useEffect(() => {
    if (job.status === "success") loadUpdate(false);
  }, [job.status]);

  const installUpdate = async () => {
    const version = updateInfo?.release?.version;
    if (!version) return;
    const confirmed = window.confirm(isEnglish
      ? `Install Ocean Connect ${version}? The application will restart automatically.`
      : `Установить Ocean Connect ${version}? Приложение будет автоматически перезапущено.`);
    if (!confirmed) return;
    setBusy(true);
    setError("");
    try {
      const response = await api.post("/admin/update/install", { version });
      setUpdateInfo((current) => ({ ...(current || {}), job: response.data?.job || { status: "queued", progress: 0 } }));
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const localized = (value) => value?.[isEnglish ? "en" : "ru"] || value?.ru || value?.en || "";
  const pendingReleases = updateInfo?.releases?.length ? updateInfo.releases : (updateInfo?.release ? [updateInfo.release] : []);

  return <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h6" sx={sectionTitleSx}>{isEnglish ? "Product update" : "Обновление продукта"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{isEnglish ? "Check for a signed release and install it automatically with health checks and rollback." : "Проверка подписанного выпуска и автоматическая установка с контролем работоспособности и откатом."}</Typography>

      <Stack spacing={2} sx={{ mt: 2.5 }}>
        <Box sx={{ ...formBlockSx, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <Box><Typography variant="body2" color="text.secondary">{isEnglish ? "Current version" : "Текущая версия"}</Typography><Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>{updateInfo?.currentVersion || "—"}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">{isEnglish ? "Available version" : "Доступная версия"}</Typography><Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>{updateInfo?.release?.version || "—"}</Typography></Box>
        </Box>

        {error ? <Alert severity="warning">{error}</Alert> : null}
        {job.status === "failed" ? <Alert severity="error">{job.message || (isEnglish ? "Update failed; the previous version was restored." : "Обновление завершилось ошибкой; предыдущая версия восстановлена.")}</Alert> : null}
        {job.status === "success" ? <Alert severity="success">{job.message || (isEnglish ? "Update installed successfully." : "Обновление успешно установлено.")}</Alert> : null}

        {jobActive ? <Box sx={formBlockSx}>
          <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}><Typography sx={{ fontWeight: 600 }}>{job.message || (isEnglish ? "Installing update" : "Установка обновления")}</Typography><Typography variant="body2" color="text.secondary">{Number(job.progress || 0)}%</Typography></Stack>
          <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, Number(job.progress || 0)))} />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>{isEnglish ? "Do not shut down the server until the operation completes." : "Не выключайте сервер до завершения операции."}</Typography>
        </Box> : null}

        {updateInfo?.updateAvailable && pendingReleases.length ? <Box sx={formBlockSx}>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{isEnglish ? "Changes included in this update" : "Изменения после установленной версии"}</Typography>
          <Stack spacing={1.75}>
            {pendingReleases.map((release, index) => {
              const notes = localized(release.notes);
              const title = localized(release.title);
              return <Box key={release.version} sx={{ pt: index ? 1.75 : 0, borderTop: index ? 1 : 0, borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 700 }}>{`Ocean Connect ${release.version}${title ? ` — ${title}` : ""}`}</Typography>
                {release.releasedAt ? <Typography variant="caption" color="text.secondary">{new Date(release.releasedAt).toLocaleString()}</Typography> : null}
                {notes ? <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-line" }}>{notes}</Typography> : null}
              </Box>;
            })}
          </Stack>
        </Box> : null}

        {!busy && updateInfo && !updateInfo.updateAvailable && !error && !jobActive ? <Alert severity="info">{isEnglish ? "The latest version is installed." : "Установлена актуальная версия."}</Alert> : null}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="flex-end">
          <Button variant="outlined" startIcon={busy ? <CircularProgress size={18} /> : <RefreshIcon />} disabled={busy || jobActive} onClick={() => loadUpdate(true)} sx={themedOutlinedButtonSx(isDark)}>{isEnglish ? "Check for updates" : "Проверить обновления"}</Button>
          {updateInfo?.updateAvailable ? <Button variant="contained" startIcon={<SystemUpdateAltIcon />} disabled={busy || jobActive} onClick={installUpdate} sx={themedPrimaryButtonSx(isDark)}>{isEnglish ? "Update" : "Обновить"}</Button> : null}
        </Stack>
      </Stack>
    </Box>
  </Paper>;
}

export default function NewAdminPanelPage({ activeSection = "general", currentLocale = "ru", currentTheme = "light", currentUserEmail, securitySettings, onSectionChange, onBack, users = [], loading, createForm, passwordDialog, deleteUserDialog, allowRememberMe, sessionDurationMinutes, defaultTheme, defaultLocale, onSecuritySettingsChange, onSecuritySettingsSave, onCheckEmailSettings, onSendTestEmail, onProfilesImported, onOpenNewUser, onCloseNewUser, onCreateUserFieldChange, onCreateUser, onRoleChange, onOpenPasswordDialog, onClosePasswordDialog, onPasswordFieldChange, onPasswordConfirmChange, onPasswordSave, onOpenDeleteDialog, onCloseDeleteDialog, onConfirmDeleteUser }) {
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isDark = currentTheme === "dark";
  const activeNavLabel = useMemo(() => {
    const item = navItems.find((entry) => entry.key === activeSection);
    return item ? item.label[currentLocale === "en" ? "en" : "ru"] : "";
  }, [activeSection, currentLocale]);

  const renderSection = () => {
    if (activeSection === "general") {
      return <GeneralSection currentLocale={currentLocale} currentTheme={currentTheme} loading={loading} defaultTheme={defaultTheme} defaultLocale={defaultLocale} onSecuritySettingsChange={onSecuritySettingsChange} onSecuritySettingsSave={onSecuritySettingsSave} />;
    }
    if (activeSection === "security") {
      return <SecuritySection currentLocale={currentLocale} currentTheme={currentTheme} loading={loading} allowRememberMe={allowRememberMe} enforceTwoFactor={securitySettings?.enforceTwoFactor} sessionDurationMinutes={sessionDurationMinutes} profileShareTtlMinutes={securitySettings?.profileShareTtlMinutes} onSecuritySettingsChange={onSecuritySettingsChange} onSecuritySettingsSave={onSecuritySettingsSave} />;
    }
    if (activeSection === "analytics") {
      return <AnalyticsSection currentLocale={currentLocale} currentTheme={currentTheme} loading={loading} settings={securitySettings || {}} onSecuritySettingsChange={onSecuritySettingsChange} onSecuritySettingsSave={onSecuritySettingsSave} />;
    }
    if (activeSection === "email") {
      return <EmailSection currentLocale={currentLocale} currentTheme={currentTheme} loading={loading} settings={securitySettings || {}} onSecuritySettingsChange={onSecuritySettingsChange} onSecuritySettingsSave={onSecuritySettingsSave} onCheckEmailSettings={onCheckEmailSettings} onOpenTestEmailDialog={() => setTestEmailDialogOpen(true)} />;
    }
    if (activeSection === "templates") {
      return <TemplatesSection currentLocale={currentLocale} currentTheme={currentTheme} loading={loading} settings={securitySettings || {}} onSecuritySettingsChange={onSecuritySettingsChange} onSecuritySettingsSave={onSecuritySettingsSave} />;
    }
    if (activeSection === "import-export") {
      return <ImportExportSection currentLocale={currentLocale} currentTheme={currentTheme} onImported={onProfilesImported} />;
    }
    if (activeSection === "update") {
      return <UpdateSection currentLocale={currentLocale} currentTheme={currentTheme} />;
    }
    return <UsersSection currentLocale={currentLocale} currentTheme={currentTheme} currentUserEmail={currentUserEmail} users={users} loading={loading} onOpenNewUser={onOpenNewUser} onRoleChange={onRoleChange} onOpenPasswordDialog={onOpenPasswordDialog} onOpenDeleteDialog={onOpenDeleteDialog} />;
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pb: 3, mt: { xs: 2, md: 3 }, flex: 1, display: "flex" }}>
      <Paper elevation={0} sx={{ ...shellCardSx, p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: { xs: "column", minWidth: "row" }, gap: 2.5, width: "100%", alignItems: "stretch", '@media (min-width:1100px)': { flexDirection: 'row' }, '@media (max-width:1099.98px)': { flexDirection: 'column' } }}>
        <Box sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0, display: "none", '@media (min-width:1100px)': { display: 'block' } }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{currentLocale === "en" ? "Administration" : "Администрирование"}</Typography>
            <Stack spacing={1}>
              {navItems.map((item) => {
                const active = item.key === activeSection;
                return (
                  <Button
                    key={item.key}
                    type="button"
                    variant={active ? "contained" : "text"}
                    onClick={() => onSectionChange(item.key)}
                    sx={{
                      ...themedSecondaryButtonSx(currentTheme === "dark"),
                      justifyContent: "flex-start",
                      borderRadius: 3,
                      minHeight: 42,
                      fontWeight: 500,
                      bgcolor: active ? (currentTheme === "dark" ? "#7a746b" : "#6b7280") : "transparent",
                      color: active ? "#fff" : "text.primary",
                      "&:hover": {
                        bgcolor: active ? (currentTheme === "dark" ? "#625d56" : "#4b5563") : "action.hover",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {item.label[currentLocale === "en" ? "en" : "ru"]}
                  </Button>
                );
              })}
            </Stack>
            <Box sx={{ mt: 2 }}>
              <Button onClick={onBack} variant="outlined" fullWidth startIcon={<ArrowBackIcon />} sx={themedOutlinedButtonSx(currentTheme === "dark")}>{currentLocale === "en" ? "Back" : "Назад"}</Button>
            </Box>
          </Paper>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "flex-start", px: { xs: 0, md: 2, xl: 4 } }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2, '@media (min-width:1100px)': { display: 'none' } }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => setMobileNavOpen(true)}
              sx={{ ...themedOutlinedButtonSx(currentTheme === "dark"), minHeight: 42 }}
            >
              {activeNavLabel}
            </Button>
          </Box>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <AnimatePresence mode="wait" initial={false}>
              <Box
                key={activeSection}
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                sx={{ width: "100%" }}
              >
                {renderSection()}
              </Box>
            </AnimatePresence>
          </Box>
        </Box>
      </Paper>
      <Dialog open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} fullWidth maxWidth="xs" sx={dialogSx}>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{currentLocale === "en" ? "Sections" : "Разделы"}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1}>
            {navItems.map((item) => {
              const active = item.key === activeSection;
              return (
                <Button
                  key={item.key}
                  type="button"
                  variant={active ? "contained" : "text"}
                  onClick={() => {
                    onSectionChange(item.key);
                    setMobileNavOpen(false);
                  }}
                  sx={{
                    ...themedSecondaryButtonSx(currentTheme === "dark"),
                    justifyContent: "flex-start",
                    borderRadius: 3,
                    minHeight: 42,
                    fontWeight: 500,
                    bgcolor: active ? (currentTheme === "dark" ? "#7a746b" : "#6b7280") : "transparent",
                    color: active ? "#fff" : "text.primary",
                    "&:hover": {
                      bgcolor: active ? (currentTheme === "dark" ? "#625d56" : "#4b5563") : "action.hover",
                      boxShadow: "none",
                    },
                  }}
                >
                  {item.label[currentLocale === "en" ? "en" : "ru"]}
                </Button>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setMobileNavOpen(false)} sx={themedSecondaryButtonSx(currentTheme === "dark")}>{t("common.cancel", currentLocale)}</Button>
        </DialogActions>
      </Dialog>
      <CreateUserDialog open={!!createForm?.open} currentLocale={currentLocale} currentTheme={currentTheme} form={createForm || {}} onClose={onCloseNewUser} onChange={onCreateUserFieldChange} onCreate={onCreateUser} loading={loading} />
      <PasswordDialog open={!!passwordDialog?.open} currentLocale={currentLocale} currentTheme={currentTheme} form={passwordDialog || {}} onClose={onClosePasswordDialog} onPasswordChange={onPasswordFieldChange} onConfirmChange={onPasswordConfirmChange} onSave={onPasswordSave} loading={loading} />
      <DeleteDialog open={!!deleteUserDialog?.open} currentLocale={currentLocale} currentTheme={currentTheme} email={deleteUserDialog?.email || ""} onClose={onCloseDeleteDialog} onConfirm={onConfirmDeleteUser} loading={loading} />
      <Dialog open={testEmailDialogOpen} onClose={loading ? undefined : () => setTestEmailDialogOpen(false)} fullWidth maxWidth="sm" sx={dialogSx}>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{currentLocale === "en" ? "Send test email" : "Отправить тестовое письмо"}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>{currentLocale === "en" ? "Enter the recipient email address for the test message." : "Введите адрес получателя для тестового письма."}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <TextField label={currentLocale === "en" ? "Recipient email" : "Email получателя"} value={testEmail} onChange={(event) => setTestEmail(event.target.value)} fullWidth sx={{ ...baseFieldSx(isDark), mt: 0.5 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTestEmailDialogOpen(false)} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>{t("common.cancel", currentLocale)}</Button>
          <Button onClick={async () => { await onSendTestEmail?.(testEmail); setTestEmailDialogOpen(false); }} variant="contained" disabled={loading || !testEmail.trim()} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Send" : "Отправить"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
